import { useRef, useState } from "react";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, ArchiveRestore, LockKeyhole, FileText, Link2, Plus, Upload } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { ProjectEmptyState, ProjectToolbar } from "@/components/ProjectLayout";
import { Avatar } from "@/components/PropertyIcons";
import { errorMessage } from "@/lib/api";
import { createDoc, updateDoc, docsQuery } from "@/lib/docs";
import { formatRelative } from "@/lib/issues";
import { useProject } from "@/lib/store";
import { toast } from "@/lib/toast";
import { TreeDropRow, TreeDropRoot, useTreeDrag } from "@/components/TreeDrag";
import { entityTree, flattenEntityTree } from "@/lib/entityTree";
import { ProjectCrumb } from "./ProjectIssues";
import { ProjectLoading, useLoadProject } from "./ProjectHome";

const route = getRouteApi("/$ws/project/$key/docs");

export function ProjectDocsPage() {
  const { t, i18n } = useTranslation();
  const { ws, key } = route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  useLoadProject(ws, key);
  const d = useProject(ws, key);
  const q = useQuery(docsQuery(ws, key));
  const [archived, setArchived] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const fileRef = useRef<HTMLInputElement>(null);
  const create = useMutation({
    mutationFn: (v: { title: string; content?: string; parent_id?: string }) => createDoc(ws, key, v),
    onSuccess: async (doc) => {
      await qc.invalidateQueries({ queryKey: ["docs", ws, key] });
      await navigate({ to: "/$ws/doc/$id", params: { ws, id: doc.id } });
    },
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });
  const canEdit = !!d?.project.role;
  const items = (q.data ?? []).filter((doc) => !doc.deleted_at && !!doc.archived_at === archived);
  const drag = useTreeDrag({
    items, disabled: !canEdit || archived,
    onExpand: (id) => setCollapsed((current) => { if (!current.has(id)) return current; const next = new Set(current); next.delete(id); return next; }),
    onMove: async (id, move) => {
      await updateDoc(id, move);
      await Promise.all([qc.invalidateQueries({ queryKey: ["docs", ws, key] }), qc.invalidateQueries({ queryKey: ["doc", id] })]);
    },
  });
  if (!d) return <ProjectLoading ws={ws} kkey={key} />;
  const rows = flattenEntityTree(entityTree(items), collapsed);

  const importFile = async (f: File) => {
    const text = await f.text();
    const title = f.name.replace(/\.(md|markdown|txt)$/i, "") || t("docs.untitled");
    create.mutate({ title, content: text });
  };

  const renderedRows = rows.map(({ item: doc, depth, children }) => (
    <TreeDropRow key={doc.id} id={doc.id} tree={drag} depth={depth}>
      <div className="group flex h-9 items-center gap-2 border-b border-border/60 px-3 text-[13px] hover:bg-hover/60" style={{ paddingLeft: 12 + depth * 20 }}>
        {drag.handle(doc, doc.title)}
        <button aria-label={collapsed.has(doc.id) ? t("tree.expand") : t("tree.collapse")} className={`w-4 text-faint ${children.length ? "" : "invisible"}`} onClick={() => setCollapsed((s) => { const c = new Set(s); if (c.has(doc.id)) c.delete(doc.id); else c.add(doc.id); return c; })}>
          {collapsed.has(doc.id) ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
        <FileText className="h-3.5 w-3.5 text-muted" />
        <Link to="/$ws/doc/$id" params={{ ws, id: doc.id }} draggable={false} className="min-w-0 flex-1 truncate hover:underline">{doc.title}</Link>
        {doc.locked_at && <LockKeyhole className="h-3 w-3 text-faint" aria-label={t("docs.locked")} />}
        {canEdit && !archived && <button className="rounded p-1 text-faint opacity-0 hover:bg-active hover:text-fg group-hover:opacity-100" title={t("docs.newChild")} onClick={() => create.mutate({ title: t("docs.untitled"), parent_id: doc.id })}><Plus className="h-3.5 w-3.5" /></button>}
        {canEdit && archived && <button className="btn btn-ghost h-7 gap-1.5 px-2 text-[12px]" onClick={() => { void updateDoc(doc.id, { archived: false }).then(() => Promise.all([qc.invalidateQueries({ queryKey: ["docs", ws, key] }), qc.invalidateQueries({ queryKey: ["doc", doc.id] })])).catch((e: unknown) => toast(errorMessage(e), { tone: "error" })); }}><ArchiveRestore className="h-3.5 w-3.5" />{t("docs.unarchive")}</button>}
        {doc.link_count > 0 && <span className="flex items-center gap-1 text-[11.5px] text-muted"><Link2 className="h-3 w-3" /> {doc.link_count}</span>}
        <span className="w-[120px] text-right text-[11.5px] text-faint">{formatRelative(doc.updated_at, i18n.language)}</span>
        <Avatar user={d.users[doc.last_editor_id ?? doc.creator_id]} size={16} />
      </div>
    </TreeDropRow>
  ));

  return (
    <>
      <PageHeader
        crumbs={[<ProjectCrumb key="p" ws={ws} d={d} />, t("nav.docs")]}
        right={
          canEdit ? (
            <>
              <input ref={fileRef} type="file" accept=".md,.markdown,.txt,text/markdown" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void importFile(f); e.target.value = ""; }} />

              <button className="btn btn-primary h-8 text-[13px]" onClick={() => create.mutate({ title: t("docs.untitled") })}><Plus className="h-3.5 w-3.5" /> {t("docs.new")}</button>
            </>
          ) : null
        }
      />
      <ProjectToolbar right={canEdit && <button className="btn h-8 text-[13px]" onClick={() => fileRef.current?.click()}><Upload className="h-3.5 w-3.5" /> {t("docs.import")}</button>}>
        <nav className="flex items-center gap-1" aria-label={t("nav.docs")}>
          {[false, true].map((value) => <button key={String(value)} aria-pressed={archived === value} onClick={() => setArchived(value)} className={`inline-flex h-8 shrink-0 items-center justify-center whitespace-nowrap rounded-full px-3 text-[13px] font-medium transition-colors ${archived === value ? "bg-active text-fg" : "text-muted hover:bg-hover hover:text-fg"}`}>{value ? "Archived" : "All"}<span className="ml-2 text-[11px] tabular-nums text-faint">{(q.data ?? []).filter((doc) => !doc.deleted_at && !!doc.archived_at === value).length}</span></button>)}
        </nav>
      </ProjectToolbar>
      {q.isPending ? (
        <div className="flex flex-1 items-center justify-center text-[13px] text-muted">{t("common.loading")}</div>
      ) : rows.length === 0 ? (
        <ProjectEmptyState title={t(archived ? "docs.archivedEmpty" : "docs.emptyTitle")} body={archived ? "" : t("docs.emptyBody")} actions={canEdit && !archived ? <button className="btn btn-primary" onClick={() => create.mutate({ title: t("docs.untitled") })}>{t("docs.new")}</button> : undefined} />
      ) : (
        <div data-tree-scroll className="flex-1 overflow-y-auto">{renderedRows}<TreeDropRoot tree={drag} /></div>
      )}
    </>
  );
}
